import React, { useRef } from 'react';
import { useAct, gsap, DEBUG, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   UYDUDAN TARLAYA, TARLADAN ÇİFTÇİYE — TEK SAHNE
   Hepsi tek sticky sahnede. Aşağıdan kayarak gelen bölüm
   yok; görüntü hep aynı yerde, sadece içerik değişiyor.

    0,0 →  3,0  uydu yörüngeden tarlaya iniyor
    3,0 →  3,5  güneş parlaması, yerinde geçiş
    3,5 →  4,7  bitki sağlığı   (mecido · yeşil-kırmızı)
    4,7 →  5,9  su durumu       (mecido · mor)
    5,9 →  7,1  klorofil        (mecido · sarı)
    7,1 →  9,1  çiftçiye giriş
    9,1 → 12,0  onun gözünden hastalık analizi

   Mecido tek videoda üç katmanı da gösteriyor, o yüzden
   ayrı bir mor video yok: ikinci mor tekrar oluyordu.
   ══════════════════════════════════════════════════════ */

const SAT = { src: '/assets/video/satellite.mp4', poster: '/assets/video/satellite-poster.jpg' };
const MECIDO = '/assets/video/mecido.mp4';
const FARMER = '/assets/video/farmer.mp4';
const DISEASE = '/assets/video/disease.mp4';

/* mecidonun üç fazı, videonun kendi zamanına denk geliyor */
const MEC_CAPS = [
  { label: 'Bitki yoğunluğu', sub: 'Farklı bölgeleri karşılaştır, saha ile birlikte değerlendir' },
  { label: 'Su durumu', sub: 'Nemli ve kuru alanları harita üzerinden gör' },
  { label: 'Klorofil', sub: 'Besin ve olgunluk okumasını incele' },
];

/* Her video 10 sn. "Video oynuyor gibi" hissini engellemek için
   her saniyeye bol kaydırma mesafesi düşüyor: bir kaydırmada az
   kare geçiyor, yani izlemiyorsun, kare kare sürüyorsun. Çiftçi
   en hızlısıydı (4,2 sn/ekran), ona en çok yeri verdik. */
const SAT_AT = 0.0, SAT_UNITS = 3.0;
const FLASH_AT = 3.0;
const MEC_AT = 3.5, MEC_STEP = 1.3, MEC_UNITS = MEC_STEP * 3;   // 3,9
const FARM_AT = MEC_AT + MEC_UNITS, FARM_UNITS = 2.6;          // 7,4
const DIS_AT = FARM_AT + FARM_UNITS, DIS_UNITS = 3.4;          // 10,0
const UNITS = DIS_AT + DIS_UNITS;                              // 12,9
const UNIT_VH = 132;

/* ── Kare ızgarasına oturan, kapılı arama sürücüsü ──────────────
   Takılmanın üç sebebi vardı, üçü de burada kapatılıyor:

   1) Aynı kareye defalarca seek. Kaydırma her karede onUpdate
      tetikliyordu; hedef 0,001 sn değişse bile yeni bir seek
      yazılıyordu. Video 12 fps olduğu için artık hedefi kare
      ızgarasına yuvarlıyoruz; kare değişmediyse seek YOK.
   2) Üst üste binen seek. Bir arama uçarken ikincisi yazılırsa
      çözücü kilitleniyor. Tek uçuş kuralı: bekleyen istek sadece
      saklanır, 'seeked' gelince sürülür.
   3) Watchdog'un kendisi. Eski sürüm zaman aşımında kilidi açıp
      HEMEN yeni seek atıyordu; tarayıcı önceki aramayı hâlâ
      işlerken bu üst üste binmeyi büyütüyordu. Artık zaman aşımı
      yalnız kilidi açar, yeni aramayı bir sonraki onUpdate sürer.
   ─────────────────────────────────────────────────────────────── */
const FPS = 12;

function makeDriver(video) {
  let busy = false, pending = null, ready = false, timer = null, lastFrame = -1;

  function clearTimer() { if (timer) { clearTimeout(timer); timer = null; } }

  /* zaman aşımı: sadece kilidi aç, yeniden arama BAŞLATMA */
  function timedOut() { timer = null; busy = false; }

  function onSeeked() {
    clearTimer();
    busy = false;
    if (pending !== null) { const t = pending; pending = null; run(t); }
  }

  function run(t) {
    busy = true;
    clearTimer();
    timer = setTimeout(timedOut, 250);
    try { video.currentTime = t; } catch (e) { clearTimer(); busy = false; }
  }

  function seek(t) {
    const d = video.duration;
    if (!d) return;
    /* kare ızgarasına yuvarla; aynı kareyse hiç dokunma */
    const frame = Math.round(t * FPS);
    if (frame === lastFrame) return;
    lastFrame = frame;
    const target = Math.min(d - 0.001, frame / FPS);
    if (busy) { pending = target; return; }
    run(target);
  }

  video.addEventListener('seeked', onSeeked);
  video.addEventListener('error', () => { pending = null; clearTimer(); busy = false; });
  video.addEventListener('loadeddata', () => { ready = true; }, { once: true });
  return { seek, isReady: () => ready, dur: () => video.duration };
}

export default function SatelliteToFields() {
  const satRef = useRef(null);
  const mecRef = useRef(null);
  const farmRef = useRef(null);
  const disRef = useRef(null);

  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    const ring = q('.v-ring')[0];

    const sat = makeDriver(satRef.current);
    const mec = makeDriver(mecRef.current);
    const farm = makeDriver(farmRef.current);
    const dis = makeDriver(disRef.current);

    /* Uydu Blob olarak iniyor: bazı sunucular parçalı indirmeyi
       desteklemiyor, o zaman her arama sıfıra düşüyor. */
    const loadSat = async () => {
      try {
        const res = await fetch(SAT.src);
        if (!res.ok || !res.body) throw new Error('video yok');
        const total = Number(res.headers.get('Content-Length')) || 7400000;
        const reader = res.body.getReader();
        const parts = [];
        let got = 0, last = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          parts.push(value);
          got += value.length;
          const now = performance.now();
          if (now - last > 100) {
            last = now;
            if (ring) ring.style.setProperty('--ld', Math.round(126 * (1 - Math.min(1, got / total))));
          }
        }
        if (ring) { ring.style.setProperty('--ld', 0); ring.classList.remove('on'); }
        satRef.current.src = URL.createObjectURL(new Blob(parts, { type: 'video/mp4' }));
        satRef.current.load();
      } catch (e) {
        /* video gelmese de bölüm eksiksiz: poster ve metin duruyor */
        if (ring) ring.classList.remove('on');
      }
    };

    const texts = q('.txt');

    if (prefersReduced()) {
      gsap.set(q('.wrap'), { opacity: 0 });
      gsap.set(q('.sat-wrap'), { opacity: 1 });
      gsap.set(texts, { opacity: 0 });
      gsap.set(q('.tb-1'), { opacity: 1 });
      gsap.set(q('.flash'), { opacity: 0 });
      return;
    }

    if (ring) ring.classList.add('on');
    loadSat();

    gsap.set(texts, { opacity: 0 });
    gsap.set([q('.mec-wrap'), q('.farm-wrap'), q('.dis-wrap')], { opacity: 0 });
    gsap.set(q('.flash'), { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        /* Sıkı scrub: Lenis kaydırmayı zaten yumuşatıyor, o yüzden
           video kaydırmaya birebir yapışabilir. Yüksek scrub'da
           (0,7) bıraktıktan sonra 0,7 sn daha akıyordu; işte o
           "kendi kendine oynuyor" hissini veriyordu. */
        scrub: 0.2,
        markers: DEBUG,
        onUpdate: (self) => {
          const t = self.progress * UNITS;
          const drive = (d, from, span) => {
            if (!d.isReady() || !d.dur()) return;
            /* Penceresinden uzaktaki videoyu aramıyoruz: her karede 4
               videoyu birden sarmak telefonu boğuyor, takılma/fırlama
               oluyor. Sadece o an görünen (ve komşusu) sarılıyor. */
            if (t < from - 0.5 || t > from + span + 0.5) return;
            const local = Math.min(1, Math.max(0, (t - from) / span));
            d.seek(local * d.dur());
          };
          drive(sat, SAT_AT, SAT_UNITS);
          drive(mec, MEC_AT, MEC_UNITS);
          drive(farm, FARM_AT, FARM_UNITS);
          drive(dis, DIS_AT, DIS_UNITS);
        },
      },
    });

    /* Çıpa: çizelgenin uzunluğunu UNITS değerine sabitler. Yoksa
       kaydırma ile çizelge eşleşmiyor ve parlama yanlış yerde patlıyor. */
    tl.set({}, {}, UNITS);

    /* metin pencereleri çakışmıyor: biri tam çıkmadan diğeri girmiyor */
    const band = (sel, inAt, outAt) => {
      const el = q(sel)[0];
      if (!el) return;
      tl.fromTo(el, { opacity: 0, y: 22 }, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.3 }, inAt);
      if (outAt !== null) tl.to(el, { opacity: 0, y: -14, ease: 'power2.in', duration: 0.26 }, outAt);
    };

    /* ── uydu ── */
    band('.tb-1', 0.15, 0.90);
    band('.tb-2', 1.25, 2.00);
    band('.tb-3', 2.35, 2.80);

    /* ── Sahne geçişi: her seferinde AYNI dil ──
       Ekran güneş ışığına boğuluyor, takas ışığın içinde oluyor,
       sonra ışık çekilirken yeni sahne ortaya çıkıyor. Ani kesme
       yok; geçiş yaklaşık 1,2 birim kaydırmaya yayılıyor. */
    const handoff = (at, outSel, inSel) => {
      tl.to(q('.flash'), { opacity: 1, ease: 'power2.in', duration: 0.55 }, at - 0.55);
      tl.to(q('.flash'), { opacity: 0, ease: 'power2.out', duration: 0.65 }, at + 0.14);
      tl.to(q(outSel), { opacity: 0, ease: 'power1.inOut', duration: 0.34 }, at - 0.06);
      tl.to(q(inSel), { opacity: 1, ease: 'power1.inOut', duration: 0.34 }, at - 0.03);
    };

    /* uydu → tarla */
    handoff(FLASH_AT, '.sat-wrap', '.mec-wrap');

    /* ── üç katman, her biri kendi biriminde ──
       Adım 1,2 birim: yazı tam bir ekran kaydırma boyunca duruyor,
       yarım kaydırmada üçü birden bitmiyor. */
    MEC_CAPS.forEach((c, i) => {
      const at = MEC_AT + i * MEC_STEP;
      band('.mc-' + i, at + 0.10, at + MEC_STEP - 0.28);
    });

    /* ── tarla → çiftçi (aynı güneş geçişi) ── */
    handoff(FARM_AT, '.mec-wrap', '.farm-wrap');
    /* yazı ışık çekildikten SONRA giriyor, parlamanın içinde değil */
    band('.tb-farm', FARM_AT + 0.85, FARM_AT + FARM_UNITS - 0.7);

    /* ── onun gözünden analiz ──
       Çiftçinin içine girdikten sonra sahne değişmiyor, aynı yerde
       birinci şahıs olarak açılıyor: görüntü yakın ve bulanık
       başlıyor, göz odağını buluyormuş gibi netleşiyor. */
    handoff(DIS_AT, '.farm-wrap', '.dis-wrap');
    tl.fromTo(q('.dis-wrap'),
      { scale: 1.2, filter: 'blur(13px)' },
      { scale: 1, filter: 'blur(0px)', ease: 'power2.out', duration: 0.75 }, DIS_AT - 0.33);

    band('.tb-d1', DIS_AT + 0.90, DIS_AT + 1.40);
    band('.tb-d2', DIS_AT + 1.80, DIS_AT + 2.30);
    band('.tb-d3', DIS_AT + 2.70, null);
  }, []);

  const vid = 'absolute inset-0 w-full h-full object-cover';

  return (
    <section
      ref={ref}
      id="uydudan"
      className="relative"
      style={{ height: `${UNITS * UNIT_VH}vh`, background: '#03040A' }}
    >
      <div className="sticky top-0 h-screen overflow-hidden" style={{ background: '#03040A' }}>
        <div className="wrap sat-wrap absolute inset-0">
          <video ref={satRef} className={vid} poster={SAT.poster} preload="none" muted playsInline aria-hidden="true" tabIndex={-1} />
        </div>

        <div className="wrap mec-wrap absolute inset-0">
          <video ref={mecRef} className={vid} src={MECIDO} preload="metadata" muted playsInline aria-hidden="true" tabIndex={-1} />
        </div>

        <div className="wrap farm-wrap absolute inset-0">
          <video ref={farmRef} className={vid} src={FARMER} preload="metadata" muted playsInline aria-hidden="true" tabIndex={-1} />
        </div>

        <div className="wrap dis-wrap absolute inset-0 will-change-transform">
          <video ref={disRef} className={vid} src={DISEASE} preload="metadata" muted playsInline aria-hidden="true" tabIndex={-1} />
        </div>

        {/* Karartma ÜSTTE: videoların kendi grafik panelleri ortada ve
            sağda duruyor, metni oraya koyunca üst üste biniyordu. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
          style={{ background: 'linear-gradient(180deg, rgba(3,4,10,.84) 0%, rgba(3,4,10,.42) 24%, rgba(3,4,10,0) 48%)' }} />

        <div className="flash absolute inset-0 pointer-events-none" aria-hidden="true"
          style={{ background: 'radial-gradient(ellipse 62% 58% at 52% 46%, #FFFFFF 0%, #FFF6DC 26%, #FFE7A8 50%, rgba(255,214,120,.6) 74%, rgba(255,196,90,0) 100%)' }} />

        <svg className="v-ring absolute right-[max(24px,4vw)] top-[max(24px,4vh)] w-9 h-9 opacity-0 transition-opacity duration-500"
          viewBox="0 0 48 48" aria-hidden="true"
          style={{ transform: 'rotate(-90deg)', color: 'rgba(200,255,34,.85)' }}>
          <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeDasharray="126" style={{ strokeDashoffset: 'var(--ld,126)' }} />
        </svg>

        <div className="absolute left-0 right-0 top-[clamp(88px,14vh,150px)] px-[max(24px,5vw)]">
          <div className="relative h-[8.4em] max-w-[min(620px,86vw)]">

            <div className="txt tb-1 absolute inset-x-0 top-0">
              <span className="font-mono text-[11px] tracking-[.14em] uppercase" style={{ color: '#C8FF22' }}>Uydu görüşü</span>
              <h2 className="mt-3 font-display font-bold text-white leading-[1.05] tracking-[-0.03em] clip-safe"
                style={{ fontSize: 'clamp(28px,4.2vw,60px)', textShadow: '0 2px 30px rgba(0,0,0,.6)' }}>
                Tarlanı sadece yerden değil,<br />yukarıdan da gör
              </h2>
            </div>

            <div className="txt tb-2 absolute inset-x-0 top-0">
              <h2 className="font-display font-bold text-white leading-[1.05] tracking-[-0.03em] clip-safe"
                style={{ fontSize: 'clamp(26px,3.8vw,54px)', textShadow: '0 2px 30px rgba(0,0,0,.6)' }}>
                Aynı bölgedeki değişimleri<br />farklı zamanlarda karşılaştır
              </h2>
            </div>

            <div className="txt tb-3 absolute inset-x-0 top-0">
              <h2 className="font-display font-bold text-white leading-[1.05] tracking-[-0.03em] clip-safe"
                style={{ fontSize: 'clamp(26px,3.8vw,54px)', textShadow: '0 2px 30px rgba(0,0,0,.6)' }}>
                Dikkat edilmesi gereken<br />alanları fark et
              </h2>
            </div>

            {MEC_CAPS.map((c, i) => (
              <div key={c.label} className={`txt mc-${i} absolute inset-x-0 top-0`}>
                <span className="block font-display font-bold text-white leading-[1.08] tracking-[-0.028em] clip-safe"
                  style={{ fontSize: 'clamp(26px,3.6vw,52px)', textShadow: '0 2px 24px rgba(0,0,0,.7)' }}>{c.label}</span>
                <span className="block mt-3 font-mono text-[12.5px] tracking-[.06em]"
                  style={{ color: 'rgba(247,247,242,.72)', textShadow: '0 1px 12px rgba(0,0,0,.7)' }}>{c.sub}</span>
              </div>
            ))}

            <div className="txt tb-farm absolute inset-x-0 top-0">
              <span className="block font-display font-bold text-white leading-[1.08] tracking-[-0.028em] clip-safe"
                style={{ fontSize: 'clamp(26px,3.6vw,52px)', textShadow: '0 2px 24px rgba(0,0,0,.7)' }}>Tarlanın içinde</span>
              <span className="block mt-3 font-mono text-[12.5px] tracking-[.06em]"
                style={{ color: 'rgba(247,247,242,.72)', textShadow: '0 1px 12px rgba(0,0,0,.7)' }}>Harita cepte · parsel parsel kontrol</span>
            </div>

            <div className="txt tb-d1 absolute inset-x-0 top-0">
              <span className="font-mono text-[11px] tracking-[.14em] uppercase" style={{ color: '#C8FF22' }}>Hastalık analizi</span>
              <h2 className="mt-3 font-display font-bold text-white leading-[1.06] tracking-[-0.03em] clip-safe"
                style={{ fontSize: 'clamp(28px,4vw,56px)', textShadow: '0 2px 28px rgba(0,0,0,.75)' }}>
                Belirtiyi büyümeden<br />fark etmeye çalış
              </h2>
            </div>

            <div className="txt tb-d2 absolute inset-x-0 top-0">
              <h2 className="font-display font-bold text-white leading-[1.06] tracking-[-0.03em] clip-safe"
                style={{ fontSize: 'clamp(26px,3.6vw,50px)', textShadow: '0 2px 28px rgba(0,0,0,.75)' }}>
                Fotoğraf üzerinden<br />olası nedenleri anla
              </h2>
            </div>

            <div className="txt tb-d3 absolute inset-x-0 top-0">
              <h2 className="font-display font-bold text-white leading-[1.12] tracking-[-0.03em] clip-safe"
                style={{ fontSize: 'clamp(20px,2.5vw,34px)', textShadow: '0 2px 28px rgba(0,0,0,.75)' }}>
                Fotoğrafı çek → Analizi başlat →<br />Sonucu incele → Tarlada kontrol et
              </h2>
              <p className="mt-4 text-[13px] leading-relaxed text-white/55 max-w-[46ch]">
                Agren AI bir ziraat mühendisinin yerini almaz. Karar vermeden önce saha koşulları ve uzman görüşü dikkate alınmalıdır.
              </p>
              <div className="mt-7">
                <a href="#demo"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[15px] text-[#EEF3E4] border border-lime/25 backdrop-blur-md"
                  style={{ background: 'rgba(11,53,30,.78)' }}>
                  Demo İste<span aria-hidden="true">›</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
